import { css } from '@emotion/react';
import dayjs from 'dayjs';

import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Checkbox from 'components/global/Checkbox';
import Icon from 'components/global/Icon';
import SelectButton from 'components/global/SelectButton';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import useCreateShipment from 'pages/Shipments/ActionForms/CreateShipment/Components/useCreateShipment';

import axios from 'services/axios';
import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import * as S from './PriceCalculator.styled';

const Ul = ({ children }) => <ul>{children}</ul>;
const Li = ({ children }) => (
	<li>
		<Typography variant="p3" color="var(--content-3)" noWrap>
			{children}
		</Typography>
	</li>
);

const LinkViewer = ({ children }) => {
	const { t } = useTranslation();
	return (
		<a href={children} rel="noopener noreferrer" target="_blank">
			{t(children)}
		</a>
	);
};

const ShippingDocumentsAndDeliveryOptionsTab = ({
	isInternationalToQatar,
	hasOptionalPickup,
	setHasOptionalPickup,
	isLockerDelivery,
	prevStep,
	nextStep,
	external,
	noticeData,
	isQuickShipment
}) => {
	const createShipment = useCreateShipment({ showDefaultAmount: !!isQuickShipment });
	const { showError } = useAlert();
	const navigate = useNavigate();
	const [isDropoff, setIsDropoff] = useState(false);
	const [isPickup, setIsPickup] = useState(false);
	const [isReadInstructions, setIsReadInstructions] = useState(isLockerDelivery);
	const { setValue, watch, getValues, setError } = useFormContext();
	const [isLoading, setIsLoading] = useState(true);
	const priceConfig = watch('priceConfig');
	const { t } = useTranslation();

	useEffect(() => {
		if (isInternationalToQatar) {
			axios
				.post(ORDER_ACTIONS.GET_ORDER_DOCUMENTS, {
					orderID: getValues('orderID')
				})
				.then((response) => {
					const { data, status } = response;
					const responseData = data?.data;

					if (status === 200 && responseData?.length > 0) {
						const frontSideIDCard = responseData.find((doc) => doc.label === 'frontSideIDCard');
						const backSideIDCard = responseData.find((doc) => doc.label === 'backSideIDCard');

						setValue('frontSideIDCard', frontSideIDCard?.url ?? null);
						setValue('backSideIDCard', backSideIDCard?.url ?? null);
					}
				})
				.catch((error) => {
					showError();
					AppLogger.error(error);
				})
				.finally(() => {
					setIsLoading(false);
				});
		} else {
			setIsLoading(false);
		}
	}, [isInternationalToQatar]);

	const pickupTypeValueSelector = useMemo(
		() =>
			getValues('priceConfig.pickupType') === 'PICKUP_BY_DC_OR_BRANCH_DROP_OF'
				? isPickup
					? 'PICKUP_BY_DC'
					: 'BRANCH_DROP_OFF'
				: getValues('priceConfig.pickupType'),
		[isPickup, isDropoff, getValues]
	);
	const handleCreateShipment = async () => {
		let isFormValid = true;

		if (!!noticeData && !isReadInstructions) {
			isFormValid = false;
		}

		if (hasOptionalPickup && !isDropoff && !isPickup) {
			showError({
				header: 'error',
				message: 'pickupOptionIsRequired'
			});
			isFormValid = false;
		}

		if (isInternationalToQatar) {
			if (!getValues('frontSideIDCard')) {
				setError('frontSideIDCard', {
					type: 'required',
					message: 'alert.requiredField'
				});
				isFormValid = false;
			}
			if (!getValues('backSideIDCard')) {
				setError('backSideIDCard', {
					type: 'required',
					message: 'alert.requiredField'
				});
				isFormValid = false;
			}
		}

		if (!isFormValid) {
			return;
		}

		setIsLoading(true);

		const boxes = getValues('boxes');
		const totalDue = getValues('totalDue');
		const { deliveryCompanySettingsId, price, otoDeliveryPriceId } = getValues('priceConfig');
		await createShipment(
			{
				items: [
					{
						orderID: getValues('orderID'),
						boxes,
						manualShipment: true,
						dcSettingsID: deliveryCompanySettingsId,
						packageWeight: Number(
							boxes.reduce((acc, curr) => Number(acc) + Number(curr.weight), 0).toFixed(1)
						),
						price,
						priceOptionID: otoDeliveryPriceId,
						pickingType: pickupTypeValueSelector || 'PICKUP_BY_DC',
						docs: isInternationalToQatar
							? [
									{
										label: 'frontSideIDCard',
										url: getValues('frontSideIDCard')
									},
									{
										label: 'backSideIDCard',
										url: getValues('backSideIDCard')
									}
								]
							: undefined,
						totalDue
					}
				]
			},
			(shipment) => {
				setValue('shipmentResultData', {
					...shipment,
					pickupType: pickupTypeValueSelector || 'PICKUP_BY_DC'
				});
				setIsLoading(false);
				nextStep();
			},
			(error) => {
				AppLogger.error(error);
				setIsLoading(false);
			},
			() => {
				setIsLoading(false);
			}
		);
	};

	const lockerDeliveryInstructions = useMemo(() => {
		if (!isLockerDelivery) {
			return [];
		}
		const steps = [];
		for (let stepNumber = 1; stepNumber < 20; stepNumber += 1) {
			let titleKey = `${watch('priceConfig.name')}.instructions${stepNumber}`;
			let descriptionKey = `${watch('priceConfig.name')}.instructions${stepNumber}.description`;
			const hasTranslation = t(titleKey, { returnObjects: true }) !== titleKey;

			if (!hasTranslation) {
				titleKey = `lockerDelivery.instructions${stepNumber}`;
				descriptionKey = `lockerDelivery.instructions${stepNumber}.description`;
			}

			if (t(titleKey) === titleKey) {
				break;
			}

			steps.push({
				title: titleKey,
				description: descriptionKey
			});
		}

		return steps;
	}, [isLockerDelivery]);

	return (
		<>
			<S.ShippingDocumentsAndPickupOptionsCard fullWidth rounded>
				<S.CardContent>
					{!isLockerDelivery && (
						<>
							<InternationalToQatarCard
								isActive={isInternationalToQatar}
								setValue={setValue}
								watch={watch}
							/>
							{isInternationalToQatar && hasOptionalPickup && <S.Line />}
							<PickupOptionsCard
								isActive={hasOptionalPickup}
								isDropoff={isDropoff}
								setIsDropoff={setIsDropoff}
								isPickup={isPickup}
								setIsPickup={setIsPickup}
								priceConfig={priceConfig}
							/>
							{!!noticeData && (isInternationalToQatar || hasOptionalPickup) && <S.Divider />}
						</>
					)}

					<InstructionsCard
						instructions={isLockerDelivery ? lockerDeliveryInstructions : noticeData?.split(',') || []}
						tooltip={!isLockerDelivery}
						setIsReadInstructions={setIsReadInstructions}
						isReadInstructions={isReadInstructions}
						isLockerDelivery={isLockerDelivery}
					/>
				</S.CardContent>
				<S.ShippingDocumentsAndPickupOptionsButtonContainer>
					{external && (
						<Button
							label="cancel"
							onClick={() => {
								navigate(-1);
							}}
							disabled={isLoading}
							variant="blackOutline"
							css={css`
								text-decoration: underline;
								border: none !important;
							`}
						/>
					)}
					<Button
						icon={faChevronLeft}
						label="previous"
						variant="outline"
						onClick={() => {
							setHasOptionalPickup(false);
							prevStep();
						}}
						disabled={isLoading}
					/>
					<Button
						icon={faChevronRight}
						iconPos="right"
						label="createShipment"
						onClick={handleCreateShipment}
						disabled={!!noticeData && !isReadInstructions}
						disabledTooltip={'readInstructionsRequiredTooltip'}
						loading={isLoading}
					/>
				</S.ShippingDocumentsAndPickupOptionsButtonContainer>
			</S.ShippingDocumentsAndPickupOptionsCard>
		</>
	);
};

const InternationalToQatarCard = ({ isActive, setValue, watch }) => {
	if (!isActive) {
		return null;
	}

	return (
		<S.ShippingDocumentsContainer>
			<Typography variant="p2" fontWeight="500">
				shippingDocuments
			</Typography>
			<Alert content="qatarShippingDocumentsAlert" showIcon />
			<FormItem label="idCardDocument" toolTip="idCardDocumentTooltip" required>
				<AdvanceFileInput
					title="frontSideHere"
					name="frontSideIDCard"
					acceptedFileType=".png,.jpg,.jpeg,"
					value={watch(`frontSideIDCard`)}
					setValue={(value) => {
						setValue(`frontSideIDCard`, value ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					maxFileSize={6}
					fileLimit={1}
				/>
				<AdvanceFileInput
					title="backSideHere"
					name="backSideIDCard"
					acceptedFileType=".png,.jpg,.jpeg,"
					value={watch(`backSideIDCard`)}
					setValue={(value) => {
						setValue(`backSideIDCard`, value ?? null, {
							shouldValidate: true,
							shouldDirty: true
						});
					}}
					maxFileSize={6}
					fileLimit={1}
				/>
			</FormItem>
		</S.ShippingDocumentsContainer>
	);
};

const PickupOptionsCard = ({ isActive, isDropoff, setIsDropoff, isPickup, setIsPickup, priceConfig }) => {
	if (!isActive) {
		return null;
	}
	return (
		<S.PickupOptionsContainer>
			<Typography variant="p2" fontWeight="500">
				pickupOptions
			</Typography>
			<S.PickupOptionsButtonContainer>
				<SelectButton
					checked={isDropoff}
					onClick={() => {
						setIsPickup(false);
						setIsDropoff(true);
					}}
					image={'https://storage.googleapis.com/tryoto-public/public/assets/images/dropoffNearestBranch.png'}
					title={'dropoffToTheNearestBranch'}
					description={
						priceConfig &&
						priceConfig.dropOffCutOff &&
						priceConfig.estimatedDropOffDate &&
						priceConfig.estimatedDropOffDeliveryDate ? (
							<Trans
								i18nKey="dropoffToTheNearestBranchDescription"
								values={{
									dropoffHour: dayjs(priceConfig.dropOffCutOff, 'HH:mm').format('HH:mm A'),
									dropoffDate: dayjs(priceConfig.estimatedDropOffDate).format('dddd, DD.MM.YYYY'),
									dropoffDeliveryDate: dayjs(priceConfig.estimatedDropOffDeliveryDate).format(
										'dddd, DD.MM.YYYY'
									)
								}}
								components={{
									blackText: <span style={{ color: 'var(--black)' }} />
								}}
							/>
						) : (
							'dropoffToTheNearestBranchDefaultDescription'
						)
					}
					badge={<Badge variant="pill" color="teel" text="fastest" />}
					variant="radio"
				/>
				<SelectButton
					checked={isPickup}
					onClick={() => {
						setIsPickup(true);
						setIsDropoff(false);
					}}
					image={'https://storage.googleapis.com/tryoto-public/public/assets/images/pickupByCourier.png'}
					title={'pickupByTheShippingCourier'}
					description={
						priceConfig && priceConfig.estimatedPickupDate ? (
							<Trans
								i18nKey="pickupByTheShippingCourierDescription"
								values={{
									estimatedPickupDate: dayjs(priceConfig.estimatedPickupDate).format(
										'dddd DD.MM.YYYY'
									)
								}}
								components={{
									blackText: <span style={{ color: 'var(--black)' }} />
								}}
							/>
						) : (
							'pickupByTheShippingCourierDefaultDescription'
						)
					}
					variant="radio"
				/>
			</S.PickupOptionsButtonContainer>
		</S.PickupOptionsContainer>
	);
};

const InstructionsCard = ({ instructions, tooltip, isReadInstructions, setIsReadInstructions, isLockerDelivery }) => {
	const { watch } = useFormContext();

	if (instructions?.length === 0) {
		return null;
	}
	return (
		<S.InstructionWrapper>
			<S.Flex>
				<Typography variant="p2" fontWeight="700">
					instructions
				</Typography>
				{tooltip && (
					<Tooltip title="liabilityDescription" placement="bottom">
						<Icon icon={faCircleInfo} color="var(--black)" />
					</Tooltip>
				)}
			</S.Flex>
			{instructions.map((instruction, index) => (
				<S.InstructionRow key={instruction?.title || instruction}>
					<Typography variant="p2" fontWeight="500" className="instruction-number">
						{index + 1}
					</Typography>

					<S.InstructionColumn>
						{instruction?.title && (
							<Typography variant="p2" fontWeight="700">
								{instruction.title || instruction}
							</Typography>
						)}
						<Typography variant="p2" className="instruction-detail" noWrap>
							<Trans
								i18nKey={instruction?.description || instruction}
								values={{
									lockerPassword: watch('priceConfig.lockerPassword')
								}}
								components={{
									linkViewer: <LinkViewer />,
									blackText: <S.BlackText />,
									ul: <Ul />,
									li: <Li />,
									p: <Typography variant="p2" fontWeight="500" noWrap />,
									bold: <Typography variant="p2" fontWeight="700" noWrap />
								}}
							/>
						</Typography>
					</S.InstructionColumn>
				</S.InstructionRow>
			))}
			{!isLockerDelivery && (
				<S.CheckboxContainer hasError={!isReadInstructions}>
					<Checkbox
						id="readInstructions"
						checked={isReadInstructions}
						onChange={(e) => setIsReadInstructions(e?.target?.checked)}
						error={!isReadInstructions}
					/>
					<Typography
						color="var(--content-3)"
						className="read-instructions"
						noWrap
						onClick={() => setIsReadInstructions(!isReadInstructions)}>
						readInstructionsDescription
					</Typography>
				</S.CheckboxContainer>
			)}
		</S.InstructionWrapper>
	);
};

export default ShippingDocumentsAndDeliveryOptionsTab;
